/**
 *  @module Resources/Subscriptions
 */
import { __assign, __awaiter, __extends, __generator } from "tslib";
import { HTTPMethod } from "../api/RestAPI.js";
import { ignoreDescriptor } from "./common/ignoreDescriptor.js";
import { Charges } from "./Charges.js";
import { CRUDResource } from "./CRUDResource.js";
export var SubscriptionPeriod;
(function (SubscriptionPeriod) {
    SubscriptionPeriod["DAILY"] = "daily";
    SubscriptionPeriod["WEEKLY"] = "weekly";
    SubscriptionPeriod["BIWEEKLY"] = "biweekly";
    SubscriptionPeriod["MONTHLY"] = "monthly";
    SubscriptionPeriod["BIMONTHLY"] = "bimonthly";
    SubscriptionPeriod["QUARTERLY"] = "quarterly";
    SubscriptionPeriod["SEMIANNUALLY"] = "semiannually";
    SubscriptionPeriod["ANNUALLY"] = "annually";
})(SubscriptionPeriod || (SubscriptionPeriod = {}));
export var SubscriptionStatus;
(function (SubscriptionStatus) {
    SubscriptionStatus["UNVERIFIED"] = "unverified";
    SubscriptionStatus["CURRENT"] = "current";
    SubscriptionStatus["SUSPENDED"] = "suspended";
    SubscriptionStatus["UNPAID"] = "unpaid";
    SubscriptionStatus["CANCELED"] = "canceled";
    SubscriptionStatus["UNCONFIRMED"] = "unconfirmed";
    SubscriptionStatus["COMPLETED"] = "completed";
})(SubscriptionStatus || (SubscriptionStatus = {}));
export var InstallmentPlan;
(function (InstallmentPlan) {
    InstallmentPlan["REVOLVING"] = "revolving";
    InstallmentPlan["FIXED_CYCLES"] = "fixed_cycles";
    InstallmentPlan["FIXED_CYCLE_AMOUNT"] = "fixed_cycle_amount";
})(InstallmentPlan || (InstallmentPlan = {}));
var ScheduledPayments = /** @class */ (function (_super) {
    __extends(ScheduledPayments, _super);
    function ScheduledPayments() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    ScheduledPayments.prototype.list = function (storeId, subscriptionsId, data, auth, callback) {
        return this.defineRoute(HTTPMethod.GET, "".concat(ScheduledPayments.routeBase))(data, callback, auth, {
            storeId: storeId,
            subscriptionsId: subscriptionsId,
        });
    };
    ScheduledPayments.prototype.get = function (storeId, subscriptionsId, id, data, auth, callback) {
        return this._getRoute()(data, callback, auth, { storeId: storeId, subscriptionsId: subscriptionsId, id: id });
    };
    ScheduledPayments.prototype.update = function (storeId, subscriptionsId, id, data, auth, callback) {
        return this._updateRoute()(data, callback, auth, { storeId: storeId, subscriptionsId: subscriptionsId, id: id });
    };
    ScheduledPayments.prototype.listCharges = function (storeId, subscriptionsId, paymentId, data, auth, callback) {
        return this.defineRoute(HTTPMethod.GET, "".concat(ScheduledPayments.routeBase, "/:paymentId/charges"))(data, callback, auth, {
            storeId: storeId,
            subscriptionsId: subscriptionsId,
            paymentId: paymentId,
        });
    };
    ScheduledPayments.routeBase = "/stores/:storeId/subscriptions/:subscriptionsId/payments";
    return ScheduledPayments;
}(CRUDResource));
export { ScheduledPayments };
var hasImmediateCharge = function (subscription) {
    var initialAmount = subscription.initialAmount, scheduleSettings = subscription.scheduleSettings;
    return !!initialAmount || !scheduleSettings || new Date(scheduleSettings.startOn) <= new Date();
};
var Subscriptions = /** @class */ (function (_super) {
    __extends(Subscriptions, _super);
    function Subscriptions(api) {
        var _this = _super.call(this, api) || this;
        _this.payments = new ScheduledPayments(api);
        _this.chargesResource = new Charges(api);
        return _this;
    }
    Subscriptions.prototype.list = function (data, auth, callback, storeId) {
        return this.defineRoute(HTTPMethod.GET, "(/stores/:storeId)/subscriptions")(data, callback, auth, { storeId: storeId });
    };
    Subscriptions.prototype.create = function (data, auth, callback) {
        var _this = this;
        return ignoreDescriptor(function (updatedData) {
            return _this.defineRoute(HTTPMethod.POST, "/subscriptions", Subscriptions.requiredParams)(updatedData, callback, auth);
        }, data);
    };
    Subscriptions.prototype.get = function (storeId, id, data, auth, callback) {
        return this._getRoute()(data, callback, auth, { storeId: storeId, id: id });
    };
    Subscriptions.prototype.update = function (storeId, id, data, auth, callback) {
        return this._updateRoute()(data, callback, auth, { storeId: storeId, id: id });
    };
    Subscriptions.prototype.delete = function (storeId, id, data, auth, callback) {
        return this._deleteRoute()(data, callback, auth, { storeId: storeId, id: id });
    };
    Subscriptions.prototype.charges = function (storeId, id, data, auth, callback) {
        return this.defineRoute(HTTPMethod.GET, "".concat(Subscriptions.routeBase, "/:id/charges"))(data, callback, auth, {
            storeId: storeId,
            id: id,
        });
    };
    Subscriptions.prototype.poll = function (storeId, id, data, auth, callback, 
    /**
     * Condition for the resource to be successfully loaded. Default to pending status check.
     */
    cancelCondition, successCondition, iterationCallback) {
        var _this = this;
        if (successCondition === void 0) { successCondition = function (_a) {
            var status = _a.status;
            return status !== SubscriptionStatus.UNVERIFIED;
        }; }
        var pollData = __assign(__assign({}, data), { polling: true });
        var promise = function () { return _this.get(storeId, id, pollData, auth); };
        return this.api.longPolling(promise, successCondition, cancelCondition, callback, undefined, undefined, iterationCallback);
    };
    Subscriptions.prototype.pollSubscriptionWithFirstCharge = function (storeId, id, data, auth, callback) {
        return __awaiter(this, void 0, void 0, function () {
            var subscription, charge, _a;
            var _this = this;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.poll(storeId, id, data, auth)];
                    case 1:
                        subscription = _b.sent();
                        if (!hasImmediateCharge(subscription)) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.api
                                .longPolling(function () { return _this.charges(storeId, id); }, function (charges) { return !!charges.items.length; })
                                .then(function (_a) {
                                var charges = _a.items;
                                return _this.chargesResource.poll(charges[0].storeId, charges[0].id, data, auth);
                            })];
                    case 2:
                        _a = _b.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        _a = null;
                        _b.label = 4;
                    case 4:
                        charge = _a;
                        callback === null || callback === void 0 ? void 0 : callback({ subscription: subscription, charge: charge });
                        return [2 /*return*/, { subscription: subscription, charge: charge }];
                }
            });
        });
    };
    Subscriptions.prototype.simulation = function (data, auth, callback, storeId) {
        return this.defineRoute(HTTPMethod.POST, "(/stores/:storeId)/subscriptions/simulate_plan", Subscriptions.requiredSimulationParams)(data, callback, auth, { storeId: storeId });
    };
    Subscriptions.requiredParams = ["transactionTokenId", "amount", "currency", "period"];
    Subscriptions.requiredSimulationParams = ["installmentPlan", "paymentType", "currency", "period"];
    Subscriptions.routeBase = "/stores/:storeId/subscriptions";
    return Subscriptions;
}(CRUDResource));
export { Subscriptions };
//# sourceMappingURL=Subscriptions.js.map